/**
 * RetentionMetricsCard Component
 * Displays user retention and churn metrics
 *
 * Story 5.4: User Retention Metrics
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Users, TrendingDown, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../../common';
import type { RetentionMetricsData, ChurnedUser } from '../../../services/reportApi';

export interface RetentionMetricsCardProps {
  /** Retention metrics data */
  data: RetentionMetricsData;
  /** Loading state */
  isLoading?: boolean;
  /** Click handler for user */
  onUserClick?: (userId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Loading skeleton
 */
const RetentionSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="grid grid-cols-3 gap-4 mb-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="text-center p-4 bg-stone-100 rounded-lg">
          <div className="h-8 bg-stone-200 rounded w-16 mx-auto mb-2" />
          <div className="h-4 bg-stone-200 rounded w-20 mx-auto" />
        </div>
      ))}
    </div>
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-16 bg-stone-100 rounded-lg" />
      ))}
    </div>
  </div>
);

/**
 * Retention metric display
 */
const RetentionMetric: React.FC<{
  label: string;
  value: number;
  benchmark?: number;
}> = ({ label, value, benchmark = 70 }) => {
  const isGood = value >= benchmark;
  const isWarning = value >= benchmark - 20 && value < benchmark;

  return (
    <div className={clsx(
      'p-4 rounded-lg text-center',
      isGood ? 'bg-green-50' :
      isWarning ? 'bg-amber-50' : 'bg-red-50'
    )}>
      <div className={clsx(
        'text-2xl font-bold',
        isGood ? 'text-green-600' :
        isWarning ? 'text-amber-600' : 'text-red-600'
      )}>
        {value}%
      </div>
      <div className="text-xs text-stone-600 mt-1">{label}</div>
    </div>
  );
};

/**
 * User list section
 */
const UserListSection: React.FC<{
  title: string;
  users: ChurnedUser[];
  variant: 'churned' | 'at-risk';
  onUserClick?: (userId: string) => void;
}> = ({ title, users, variant, onUserClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (users.length === 0) return null;

  const Icon = variant === 'churned' ? TrendingDown : AlertTriangle;
  const colorClasses = variant === 'churned'
    ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';

  return (
    <div className={clsx('border rounded-lg', colorClasses.split(' ')[2])}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx('w-full flex items-center justify-between p-3 rounded-t-lg', colorClasses.split(' ')[0])}
      >
        <div className="flex items-center gap-2">
          <Icon className={clsx('w-4 h-4', colorClasses.split(' ')[1])} />
          <span className={clsx('text-sm font-medium', colorClasses.split(' ')[1])}>
            {title}
          </span>
          <span className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
            variant === 'churned' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          )}>
            {users.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {isExpanded && (
        <div className="p-3 max-h-48 overflow-y-auto">
          <div className="space-y-2">
            {users.slice(0, 10).map((user) => (
              <button
                key={user.userId}
                onClick={() => onUserClick?.(user.userId)}
                className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-white transition-colors text-left"
              >
                <div>
                  <div className="text-sm font-medium text-stone-900">{user.name}</div>
                  <div className="text-xs text-stone-500">
                    {user.email} · {user.role}
                  </div>
                </div>
                <span className={clsx(
                  'text-xs font-medium',
                  variant === 'churned' ? 'text-red-600' : 'text-amber-600'
                )}>
                  {user.lastLoginDaysAgo}d ago
                </span>
              </button>
            ))}
            {users.length > 10 && (
              <p className="text-xs text-stone-500 text-center pt-2">
                +{users.length - 10} more users
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Retention Metrics Card Component
 */
export const RetentionMetricsCard: React.FC<RetentionMetricsCardProps> = ({
  data,
  isLoading = false,
  onUserClick,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Retention Metrics</h3>
        <RetentionSkeleton />
      </Card>
    );
  }

  const {
    day7Retention,
    day30Retention,
    day90Retention,
    churnedUsers,
    atRiskUsers,
    totalActiveUsers,
    totalChurnedUsers,
    churnRate,
  } = data;

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">Retention Metrics</h3>
          <p className="text-sm text-stone-500">
            Track user retention and identify churn risks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-stone-400" />
          <span className="text-sm text-stone-600">{totalActiveUsers} active</span>
        </div>
      </div>

      {/* Retention rates */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <RetentionMetric label="7-Day Retention" value={day7Retention} benchmark={80} />
        <RetentionMetric label="30-Day Retention" value={day30Retention} benchmark={70} />
        <RetentionMetric label="90-Day Retention" value={day90Retention} benchmark={60} />
      </div>

      {/* Churn summary */}
      <div className="p-4 bg-stone-50 rounded-lg mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-stone-700">Churn Rate</div>
            <div className="text-xs text-stone-500">Users inactive for 30+ days</div>
          </div>
          <div className="text-right">
            <div className={clsx(
              'text-2xl font-bold',
              churnRate <= 10 ? 'text-green-600' :
              churnRate <= 20 ? 'text-amber-600' : 'text-red-600'
            )}>
              {churnRate}%
            </div>
            <div className="text-xs text-stone-500">
              {totalChurnedUsers} users
            </div>
          </div>
        </div>
      </div>

      {/* User lists */}
      <div className="space-y-3">
        <UserListSection
          title="At-Risk Users (14-30 days inactive)"
          users={atRiskUsers}
          variant="at-risk"
          onUserClick={onUserClick}
        />
        <UserListSection
          title="Churned Users (30+ days inactive)"
          users={churnedUsers}
          variant="churned"
          onUserClick={onUserClick}
        />
      </div>

      {/* Empty state */}
      {churnedUsers.length === 0 && atRiskUsers.length === 0 && (
        <div className="text-center py-6 text-green-600">
          <Users className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm font-medium">Great! No at-risk or churned users.</p>
        </div>
      )}
    </Card>
  );
};

export default RetentionMetricsCard;
